import React, { useState, useEffect } from 'react';
import { Team } from '../api/teamsApi';
import { Match, MatchFormData, MatchStatus } from '../api/matchesApi';
import { format, parseISO } from 'date-fns';

interface MatchFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (matchData: MatchFormData) => Promise<void>;
  teams: Team[];
  match?: Match; // Optional match for editing
  title: string;
}

const MatchFormModal: React.FC<MatchFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  teams,
  match,
  title
}) => {
  const [formData, setFormData] = useState<MatchFormData>({
    id: '',
    source_id: '',
    round: '',
    matchday: null,
    home_team_id: null,
    away_team_id: null,
    home_team_placeholder: '',
    away_team_placeholder: '',
    home_team_score: null,
    away_team_score: null,
    result: '',
    status: MatchStatus.SCHEDULED,
    scorers: [],
    home_team_yellow_cards: 0,
    home_team_second_yellow_cards: 0,
    home_team_red_cards: 0,
    away_team_yellow_cards: 0,
    away_team_second_yellow_cards: 0,
    away_team_red_cards: 0,
    match_date: new Date().toISOString(),
    group_id: null,
    is_last_groups_match: false,
    bettable: false,
  });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [dateTimeLocal, setDateTimeLocal] = useState<string>('');

  // Set form data when editing a match
  useEffect(() => {
    if (match) {
      setFormData({
        id: match.id,
        source_id: match.source_id,
        round: match.round,
        matchday: match.matchday,
        home_team_id: match.home_team_id,
        away_team_id: match.away_team_id,
        home_team_placeholder: match.home_team_placeholder,
        away_team_placeholder: match.away_team_placeholder,
        home_team_score: match.home_team_score,
        away_team_score: match.away_team_score,
        winner: match.winner,
        status: match.status || MatchStatus.SCHEDULED,
        scorers: match.scorers,
        home_team_yellow_cards: match.home_team_yellow_cards,
        home_team_second_yellow_cards: match.home_team_second_yellow_cards,
        home_team_red_cards: match.home_team_red_cards,
        away_team_yellow_cards: match.away_team_yellow_cards,
        away_team_second_yellow_cards: match.away_team_second_yellow_cards,
        away_team_red_cards: match.away_team_red_cards,
        match_date: match.match_date,
        group_id: match.group_id,
        knockout_next_match: match.knockout_next_match,
        knockout_next_match_home: match.knockout_next_match_home,
        is_last_groups_match: match.is_last_groups_match,
        bettable: match.bettable,
      });
      
      // Convert ISO date to local datetime format for the input
      try {
        setDateTimeLocal(format(parseISO(match.match_date), "yyyy-MM-dd'T'HH:mm"));
      } catch (e) {
        console.error("Error parsing date:", e);
        setDateTimeLocal(format(new Date(), "yyyy-MM-dd'T'HH:mm"));
      }
    } else {
      // Reset form when adding a new match
      const now = new Date();
      
      setFormData({
        id: `M${Date.now()}`, // Generate a unique ID
        source_id: '',
        round: 'GROUP',
        matchday: null,
        home_team_id: null,
        away_team_id: null,
        home_team_placeholder: 'TBD',
        away_team_placeholder: 'TBD',
        home_team_score: null,
        away_team_score: null,
        result: '',
        status: MatchStatus.SCHEDULED,
        scorers: [],
        home_team_yellow_cards: 0,
        home_team_second_yellow_cards: 0,
        home_team_red_cards: 0,
        away_team_yellow_cards: 0,
        away_team_second_yellow_cards: 0,
        away_team_red_cards: 0,
        match_date: now.toISOString(),
        group_id: null,
        knockout_next_match: null,
        knockout_next_match_home: false,
        is_last_groups_match: false,
        bettable: false,
      });
      
      setDateTimeLocal(format(now, "yyyy-MM-dd'T'HH:mm"));
    }
  }, [match, isOpen]);

  // Function to get team name by ID
  const getTeamName = (teamId: number | null): string => {
    if (!teamId) return '';
    const team = teams.find((t) => t.id === teamId);
    return team ? team.name : 'Unknown Team';
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement >) => {
    const { name, value } = e.target;
    
    if (name === 'home_team_id' || name === 'away_team_id') {
      // Handle team selection
      setFormData({
        ...formData,
        [name]: value === '' ? null : parseInt(value, 10),
      });
    } else if (name === 'home_team_score' || name === 'away_team_score') {
      // Handle score inputs
      setFormData({
        ...formData,
        [name]: value === '' ? null : parseInt(value, 10),
      });
    } else if (name === 'matchday') {
      // Handle matchday input - can be null
      setFormData({
        ...formData,
        [name]: value === '' ? null : parseInt(value, 10),
      });
    } else if (name.includes('_cards')) {
      // Handle card inputs
      setFormData({
        ...formData,
        [name]: parseInt(value, 10) || 0,
      });
    } else if (name === 'result') {
      // Handle result selection - set to null if empty
      setFormData({
        ...formData,
        [name]: value === '' ? '' : value,
      });
    } else if (name === 'match_date_local') {
      // Handle date input separately
      setDateTimeLocal(value);
      
      // Convert local datetime to ISO format for the API
      try {
        const date = new Date(value);
        setFormData({
          ...formData,
          match_date: date.toISOString(),
        });
      } catch (e) {
        console.error("Error converting date:", e);
      }
    } else if (name === 'status') {
      // Handle status selection
      setFormData({
        ...formData,
        [name]: value as MatchStatus,
      });
    } else if (name === 'winner') {
      setFormData({
        ...formData,
        [name]: value === '' ? null : parseInt(value, 10),
      });
    } else if (name === 'source_id') {
      setFormData({
        ...formData,
        [name]: value,
      });
    } else if (name === 'knockout_next_match') {
      setFormData({
        ...formData,
        [name]: value === '' ? null : value,
      });
    } else if (name === 'knockout_next_match_home' && (e.target instanceof HTMLInputElement && e.target.type === 'checkbox')) {
      setFormData({
        ...formData,
        [name]: e.target.checked,
      });
    } else {
      // Handle other text inputs
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await onSubmit(formData);
      onClose();
    } catch (err) {
      console.error('Error submitting match form:', err);
      setError('Failed to save match. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-dark-100 rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-screen overflow-y-auto">
        <div className="px-6 py-4 border-b border-dark-200">
          <h2 className="text-xl font-semibold text-white">{title}</h2>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-900 bg-opacity-20 border border-red-500 text-red-200 rounded">
              {error}
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Source ID</label>
              <input
                type="text"
                name="source_id"
                value={formData.source_id}
                onChange={handleChange}
                className="w-full p-2 bg-dark-200 border border-dark-300 rounded text-white focus:ring-stake-blue focus:border-stake-blue"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Matchday</label>
              <input
                type="number"
                name="matchday"
                value={formData.matchday === null ? '' : formData.matchday}
                onChange={handleChange}
                className="w-full p-2 bg-dark-200 border border-dark-300 rounded text-white focus:ring-stake-blue focus:border-stake-blue"
                min="1"
                placeholder="Optional"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Round</label>
              <select
                name="round"
                value={formData.round}
                onChange={handleChange}
                className="w-full p-2 bg-dark-200 border border-dark-300 rounded text-white focus:ring-stake-blue focus:border-stake-blue"
                required
              >
                <option value="GROUP">Group Stage</option>
                <option value="ROUND_OF_32">Round of 32</option>
                <option value="ROUND_OF_16">Round of 16</option>
                <option value="QUARTER_FINAL">Quarter-Final</option>
                <option value="SEMI_FINAL">Semi-Final</option>
                <option value="THIRD_PLACE">Third Place</option>
                <option value="FINAL">Final</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full p-2 bg-dark-200 border border-dark-300 rounded text-white focus:ring-stake-blue focus:border-stake-blue"
                required
              >
                <option value={MatchStatus.SCHEDULED}>Scheduled</option>
                <option value={MatchStatus.LIVE}>Live</option>
                <option value={MatchStatus.COMPLETED}>Completed</option>
              </select>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Home Team</label>
              <select
                name="home_team_id"
                value={formData.home_team_id === null ? '' : formData.home_team_id}
                onChange={handleChange}
                className="w-full p-2 bg-dark-200 border border-dark-300 rounded text-white focus:ring-stake-blue focus:border-stake-blue"
              >
                <option value="">Select Team</option>
                {teams.map(team => (
                  <option key={`home-${team.id}`} value={team.id}>{team.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Away Team</label>
              <select
                name="away_team_id"
                value={formData.away_team_id === null ? '' : formData.away_team_id}
                onChange={handleChange}
                className="w-full p-2 bg-dark-200 border border-dark-300 rounded text-white focus:ring-stake-blue focus:border-stake-blue"
              >
                <option value="">Select Team</option>
                {teams.map(team => (
                  <option key={`away-${team.id}`} value={team.id}>{team.name}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Home Team Placeholder</label>
              <input
                type="text"
                name="home_team_placeholder"
                value={formData.home_team_placeholder}
                onChange={handleChange}
                className="w-full p-2 bg-dark-200 border border-dark-300 rounded text-white focus:ring-stake-blue focus:border-stake-blue"
                placeholder="Used when team is not determined"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Away Team Placeholder</label>
              <input
                type="text"
                name="away_team_placeholder"
                value={formData.away_team_placeholder}
                onChange={handleChange}
                className="w-full p-2 bg-dark-200 border border-dark-300 rounded text-white focus:ring-stake-blue focus:border-stake-blue"
                placeholder="Used when team is not determined"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Home Team Score</label>
              <input
                type="number"
                name="home_team_score"
                value={formData.home_team_score === null ? '' : formData.home_team_score}
                onChange={handleChange}
                min="0"
                className="w-full p-2 bg-dark-200 border border-dark-300 rounded text-white focus:ring-stake-blue focus:border-stake-blue"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Away Team Score</label>
              <input
                type="number"
                name="away_team_score"
                value={formData.away_team_score === null ? '' : formData.away_team_score}
                onChange={handleChange}
                min="0"
                className="w-full p-2 bg-dark-200 border border-dark-300 rounded text-white focus:ring-stake-blue focus:border-stake-blue"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Winner</label>
              <select
                name="winner"
                value={formData.winner || ''}
                onChange={handleChange}
                className="w-full p-2 bg-dark-200 border border-dark-300 rounded text-white focus:ring-stake-blue focus:border-stake-blue"
              >
                <option value="">Select Winner</option>
                <option value="draw">Draw</option>
                {formData.home_team_id && (
                  <option value={formData.home_team_id.toString()}>
                    {getTeamName(formData.home_team_id)}
                  </option>
                )}
                {formData.away_team_id && (
                  <option value={formData.away_team_id.toString()}>
                    {getTeamName(formData.away_team_id)}
                  </option>
                )}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Match Date & Time</label>
              <input
                type="datetime-local"
                name="match_date_local"
                value={dateTimeLocal}
                onChange={handleChange}
                className="w-full p-2 bg-dark-200 border border-dark-300 rounded text-white focus:ring-stake-blue focus:border-stake-blue"
                required
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Home Yellow Cards</label>
              <input
                type="number"
                name="home_team_yellow_cards"
                value={formData.home_team_yellow_cards}
                onChange={handleChange}
                min="0"
                className="w-full p-2 bg-dark-200 border border-dark-300 rounded text-white focus:ring-stake-blue focus:border-stake-blue"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Away Yellow Cards</label>
              <input
                type="number"
                name="away_team_yellow_cards"
                value={formData.away_team_yellow_cards}
                onChange={handleChange}
                min="0"
                className="w-full p-2 bg-dark-200 border border-dark-300 rounded text-white focus:ring-stake-blue focus:border-stake-blue"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Home Second Yellow Cards</label>
              <input
                type="number"
                name="home_team_second_yellow_cards"
                value={formData.home_team_second_yellow_cards}
                onChange={handleChange}
                min="0"
                className="w-full p-2 bg-dark-200 border border-dark-300 rounded text-white focus:ring-stake-blue focus:border-stake-blue"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Away Second Yellow Cards</label>
              <input
                type="number"
                name="away_team_second_yellow_cards"
                value={formData.away_team_second_yellow_cards}
                onChange={handleChange}
                min="0"
                className="w-full p-2 bg-dark-200 border border-dark-300 rounded text-white focus:ring-stake-blue focus:border-stake-blue"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Home Red Cards</label>
              <input
                type="number"
                name="home_team_red_cards"
                value={formData.home_team_red_cards}
                onChange={handleChange}
                min="0"
                className="w-full p-2 bg-dark-200 border border-dark-300 rounded text-white focus:ring-stake-blue focus:border-stake-blue"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Away Red Cards</label>
              <input
                type="number"
                name="away_team_red_cards"
                value={formData.away_team_red_cards}
                onChange={handleChange}
                min="0"
                className="w-full p-2 bg-dark-200 border border-dark-300 rounded text-white focus:ring-stake-blue focus:border-stake-blue"
              />
            </div>
          </div>

          { formData.group_id === null && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Qualifying to match</label>
                <input
                  type="text"
                  name="knockout_next_match"
                  value={formData.knockout_next_match || ''}
                  onChange={handleChange}
                  className="w-full p-2 bg-dark-200 border border-dark-300 rounded text-white focus:ring-stake-blue focus:border-stake-blue"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Home team in the next match</label>
                <input
                  type="checkbox"
                  name="knockout_next_match_home"
                  checked={formData.knockout_next_match_home || false}
                  onChange={handleChange}
                  className="w-full p-2 bg-dark-200 border border-dark-300 rounded text-white focus:ring-stake-blue focus:border-stake-blue"
                />
              </div>
            </div>
          )}
          
          
          <div className="flex justify-end space-x-3 border-t border-dark-200 pt-4">
            <button
              type="button"
              className="px-4 py-2 text-sm font-medium text-gray-300 bg-dark-200 border border-dark-300 rounded-md shadow-sm hover:bg-dark-300 focus:outline-none focus:ring-2 focus:ring-stake-blue focus:ring-offset-2 focus:ring-offset-dark-100"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-stake-blue border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-stake-blue focus:ring-offset-2 focus:ring-offset-dark-100"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save Match'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MatchFormModal; 